import { EventCategory }                 from '../EventCategory/EventCategory';
import { Organizer }                     from '../Organizer/Organizer';
import { Image }                         from '../Image/Image';
import { Location }                      from '../Location/Location';
import { IFileReference, FileReference } from '../FileReference/FileReference';
import { createUniqKey, sortImages }     from 'Utils';
import { EventTicket, IEventTicket }     from '../EventTicket/EventTicket';

export  type TEventTicketDate = {
  date: Date,
  start: string,
  end: string|null,
}

export type TEventLink = {
  id: string,
  label: string|null,
  ref: string|null,
  _update?: boolean,
  _delete?: boolean,
}

export interface IEvent {
  id: string,
  name: string,
  description: string,
  location: Location | null,
  multiDay: boolean,
  organisation: Organizer | null,
  images: Image[] | IFileReference[],
  tickets: IEventTicket[],
  links: TEventLink[],
  categories: EventCategory[],
  createdAt: Date | null,
  deletedAt: Date | null,
  canceltAt: Date | null,
  startAt: Date | null,
  endAt: Date | null,

  views: number,
  externalTicketing: boolean,
  externalTicketingUrl: string,
  hidden: boolean,
  purchaseable: boolean,
  type: 'onetime' | 'start_end' | 'multiple',
  dates: TEventTicketDate[],
  startDate: Date | null,
  slug: string | null,
  ticketPercentageFeeOverride: string | null,
  startStartTime: string | null,
  startEndTime: string | null,
  externalSource: string | null,
  externalUid: string | null,

  endDate: Date | null,
  endStartTime: string | null,
  endEndTime: string | null,
}

export class Event {
  public _key: string;
  public id: string;
  public name: string;
  public description: string;
  public externalTicketing: boolean;
  public externalTicketingUrl: string;
  public location: Location | null;
  public multiDay: boolean;
  public organisation: Organizer | null;
  public images: Image[];
  public categories: EventCategory[];
  public tickets: EventTicket[];
  public links: TEventLink[];
  public createdAt: Date | null;
  public deletedAt: Date | null;
  public startAt: Date | null;
  public endAt: Date | null;
  public views: number;
  public hidden: boolean;
  public purchaseable: boolean;

  public type: 'onetime' | 'start_end' | 'multiple';
  public dates: TEventTicketDate[];
  public startDate: Date | null;
  public canceltAt: Date | null;
  public startStartTime: string | null;
  public startEndTime: string | null;
  public endDate: Date | null;
  public ticketPercentageFeeOverride: string | null;
  public endStartTime: string | null;
  public endEndTime: string | null;
  public slug: string | null;
  public externalSource: string | null;
  public externalUid: string | null;


  constructor(data: IEvent) {
    this._key = createUniqKey();

    this.id = data.id;
    this.name = data.name;
    this.description = data.description;
    this.externalTicketing = data.externalTicketing;
    this.externalTicketingUrl = data.externalTicketingUrl;
    this.location = data.location ? data.location : null;
    this.organisation = data.organisation ? new Organizer(data.organisation) : null;
    this.multiDay = 'multiDay' in data ? data.multiDay : false;
    this.categories = data.categories ? data.categories.map((category) => new EventCategory(category)) : [];
    this.tickets = data.tickets ? data.tickets.map((ticket) => new EventTicket(ticket)) : [];
    this.images = sortImages(data.images ? data.images.map((image) => new FileReference(image as IFileReference)) : []);
    this.createdAt = data.createdAt ? new Date(data.createdAt as unknown as number * 1000) : null;
    this.canceltAt = data.canceltAt ? new Date(data.canceltAt as unknown as number * 1000) : null;
    this.deletedAt = data.deletedAt ? new Date(data.deletedAt as unknown as number * 1000) : null;
    this.startAt = data.startAt ? new Date(data.startAt as unknown as number * 1000) : null;
    this.endAt = data.endAt ? new Date(data.endAt as unknown as number * 1000) : null;
    this.links = data.links ? data.links : [];
    this.views = data.views ?? 0;
    this.hidden = data.hidden ?? false;
    this.purchaseable = data.purchaseable ?? true;
    this.type = data.type || 'onetime';
    this.dates = data.dates ? data.dates : [];
    this.dates.sort((a, b) => {
      const dateA = a.date instanceof Date ? a.date : new Date(a.date);
      const startTimeA = (a.start ? a.start : '00:00').split(':');
      dateA.setHours(startTimeA[0] ? parseInt(startTimeA[0], 10) : 0, startTimeA[1] ? parseInt(startTimeA[1], 10) : 0, 0, 0);

      const dateB = b.date instanceof Date ? b.date : new Date(b.date);
      const startTimeB = (b.start ? b.start : '00:00').split(':');
      dateB.setHours(startTimeB[0] ? parseInt(startTimeB[0], 10) : 0, startTimeB[1] ? parseInt(startTimeB[1], 10) : 0, 0, 0);

      return dateA.getTime() - dateB.getTime();
    })
    this.startDate = data.startDate ? new Date(data.startDate as unknown as number * 1000) : null;
    this.startStartTime = data.startStartTime;
    this.ticketPercentageFeeOverride = data.ticketPercentageFeeOverride;
    this.startEndTime = data.startEndTime;
    this.endDate = data.endDate ? new Date(data.endDate as unknown as number * 1000) : null;
    this.endStartTime = data.endStartTime;
    this.endEndTime = data.endEndTime;
    this.slug = data.slug;
    this.externalSource = data.externalSource;
    this.externalUid = data.externalUid;

  }
}

export const createNewEvent = (data: Partial<IEvent> = {}): Event => new Event({
  id: data.id || 'new',
  name: data.name || '',
  description: data.description || '',
  externalTicketing: data.externalTicketing || false,
  externalTicketingUrl: data.externalTicketingUrl || '',
  views: data.views || 0,
  location: data.location || null,
  multiDay: 'multiDay' in data ? data.multiDay as boolean : false,
  categories: data.categories ? data.categories : [],
  tickets: data.tickets ? data.tickets : [],
  links: data.links ? data.links : [],
  images: data.images ? data.images : [],
  organisation: data.organisation ? data.organisation : null,
  createdAt: data.createdAt || null,
  canceltAt: data.canceltAt || null,
  deletedAt: data.deletedAt || null,
  startAt: data.startAt || null,
  endAt: data.endAt || null,
  hidden: data.hidden ?? false,
  purchaseable: data.purchaseable ?? true,
  type: data.type || 'onetime',
  dates: data.dates || [],
  startDate: data.startDate || null,
  startStartTime: data.startStartTime || null,
  startEndTime: data.startEndTime || null,
  endDate: data.endDate || null,
  endStartTime: data.endStartTime || null,
  endEndTime: data.endEndTime || null,
  externalSource: data.externalSource || null,
  slug: data.slug || null,
  externalUid: data.externalUid || null,
  ticketPercentageFeeOverride: data.ticketPercentageFeeOverride || null,
});
